import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    MemberItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextInput,
    StateCard,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    UsersIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Members = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        members,
        setMembers
    ] = useState<MemberItem[]>([]);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        previewItem,
        setPreviewItem
    ] = useState<MemberItem | null>(null);

    const [
        formData,
        setFormData
    ] = useState({
        name_surname: "",
        address: "",
        phone: "",
        email: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        pagination,
        setPagination
    ] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        limit: 10
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPagination(prev => ({
                ...prev,
                currentPage: 1
            }));
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchMembers = useCallback(async (page: number = 1) => {
        try {
            setIsLoading(true);
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/members`);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", pagination.limit.toString());

            if (debouncedSearch) {
                url.searchParams.append("search", debouncedSearch);
            }

            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setMembers(result.data);
                if (result.meta) {
                    setPagination(result.meta);
                }
            }
        } catch (error) {
            console.error("Üyeler alınamadı:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeOrganizationId,
        debouncedSearch,
        user?.role,
        pagination.limit
    ]);

    useEffect(() => {
        if (user?.role === "root" && !activeOrganizationId) {
            setMembers([]);
            setPagination({
                totalRecords: 0,
                currentPage: 1,
                totalPages: 1,
                limit: 10
            });
            return;
        }

        fetchMembers(pagination.currentPage);
    }, [
        activeOrganizationId,
        fetchMembers,
        pagination.currentPage,
        user?.role
    ]);

    const handleSave = async () => {
        if (!formData.name_surname) {
            NCoreUIKitToast.open({
                title: localize("members-alert-name-required")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId ? `${process.env.API_URL}/members/${editId}` : `${process.env.API_URL}/members`;
            const method = editId ? "PUT" : "POST";

            const bodyData: Record<string, unknown> = {
                ...formData,
                ...(activeOrganizationId && user?.role === "root" ? {
                    organization_id: activeOrganizationId
                } : {})
            };

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(bodyData),
                method
            });

            const result = await response.json();

            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    name_surname: "",
                    address: "",
                    phone: "",
                    email: ""
                });
                fetchMembers(1);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || localize("members-toast-unknown-error")
                });
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }: { closeAnimation: (cb?: () => void) => void }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const url = `${process.env.API_URL}/members/${id}`;

                        const response = await fetch(url, {
                            headers: {
                                "Content-Type": "application/json",
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();

                        if (result.status === "success") {
                            closeAnimation(() => {
                                NCoreUIKitToast.open({
                                    title: localize("members-toast-delete-success")
                                });
                                fetchMembers(1);
                            });
                        } else {
                            closeAnimation(() => {
                                NCoreUIKitToast.open({
                                    title: result.message || localize("members-toast-delete-failed")
                                });
                            });
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                        closeAnimation();
                    } finally {
                        isDeleting = false;
                    }
                },
                title: localize("members-dialog-delete-primary"),
                type: "danger"
            },
            secondaryButtonProps: {
                onPress: ({
                    closeAnimation
                }: { closeAnimation: (cb?: () => void) => void }) => closeAnimation(),
                title: localize("members-dialog-delete-secondary"),
                type: "neutral"
            },
            content: localize("members-dialog-delete-content", [
                name
            ]),
            title: localize("members-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const renderPagination = () => {
        if (pagination.totalPages <= 1) return null;

        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginBottom: spaces.spacingXl,
                    marginTop: spaces.spacingXl,
                    gap: spaces.spacingMd,
                    flexWrap: "wrap"
                }
            ]}
        >
            <Button
                isDisabled={pagination.currentPage === 1 || isLoading}
                title={localize("members-pagination-prev")}
                onPress={() => setPagination(prev => ({
                    ...prev,
                    currentPage: prev.currentPage - 1
                }))}
                type="neutral"
            />
            <Text>
                {localize("members-pagination-info", [
                    pagination.currentPage.toString(),
                    pagination.totalPages.toString()
                ])}
            </Text>
            <Button
                isDisabled={pagination.currentPage === pagination.totalPages || isLoading}
                title={localize("members-pagination-next")}
                onPress={() => setPagination(prev => ({
                    ...prev,
                    currentPage: prev.currentPage + 1
                }))}
                type="neutral"
            />
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("members-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchMembers(pagination.currentPage);
                            setIsRefreshing(false);
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        isLoading={isRefreshing}
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("members-page-add-button")}
                        onPress={() => {
                            setEditId(null);
                            setFormData({
                                name_surname: "",
                                address: "",
                                phone: "",
                                email: ""
                            });
                            setModalVisible(true);
                        }}
                        type="primary"
                    />
                </View>
            </View>
            <TextInput
                placeholder={localize("members-search-placeholder")}
                style={{
                    ...stylesheet.searchInput,
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                isLoading && members.length === 0 ?
                    <ActivityIndicator
                        size="large"
                    />
                :
                    <>
                        {
                            members.length === 0 ?
                                <StateCard
                                    icon={({
                                        color,
                                        size
                                    }: { color: string; size: number }) => {
                                        return <UsersIcon
                                            color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                            size={size}
                                        />;
                                    }}
                                    subTitle={localize("members-list-empty-subtitle")}
                                    title={localize("members-list-empty-title")}
                                />
                            :
                                <ScrollView
                                    contentContainerStyle={[
                                        stylesheet.listContent,
                                        {
                                            paddingBottom: spaces.spacingXl,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    showsVerticalScrollIndicator={false}
                                >
                                    {members.map(item => {
                                        return <TouchableOpacity
                                            style={[
                                                stylesheet.itemContainer,
                                                {
                                                    backgroundColor: colors.content.container.mid,
                                                    alignItems: isMobile ? "stretch" : "center",
                                                    flexDirection: isMobile ? "column" : "row",
                                                    borderRadius: radiuses.md,
                                                    padding: spaces.spacingMd,
                                                    gap: spaces.spacingMd
                                                }
                                            ]}
                                            key={item.id}
                                        >
                                            <View
                                                style={[
                                                    stylesheet.itemContent,
                                                    {
                                                        gap: spaces.spacingMd
                                                    }
                                                ]}
                                            >
                                                <TouchableOpacity
                                                    onPress={() => setPreviewItem(item)}
                                                    style={[
                                                        stylesheet.itemContent,
                                                        {
                                                            gap: spaces.spacingMd
                                                        }
                                                    ]}
                                                >
                                                    <View
                                                        style={[
                                                            stylesheet.itemIcon,
                                                            {
                                                                backgroundColor: colors.content.container.default,
                                                                borderRadius: radiuses.full
                                                            }
                                                        ]}
                                                    >
                                                        <UsersIcon
                                                            color={colors.content.icon.emphasized}
                                                            size={24}
                                                        />
                                                    </View>
                                                    <View
                                                        style={[
                                                            stylesheet.itemInfo,
                                                            {
                                                                gap: spaces.spacingSm
                                                            }
                                                        ]}
                                                    >
                                                        <Text
                                                            variant="titleMediumSize"
                                                        >
                                                            {item.name_surname}
                                                        </Text>
                                                        {(item.email || item.phone) && <Text
                                                            variant="bodyMediumSize"
                                                            color="mid"
                                                        >
                                                            {item.email} {item.phone ? `- ${item.phone}` : ""}
                                                        </Text>}
                                                    </View>
                                                </TouchableOpacity>
                                            </View>
                                            <View
                                                style={[
                                                    stylesheet.itemActions,
                                                    {
                                                        alignItems: isMobile ? "stretch" : "center",
                                                        flexDirection: isMobile ? "column" : "row",
                                                        gap: spaces.spacingMd
                                                    }
                                                ]}
                                            >
                                                <Button
                                                    icon={({
                                                        color,
                                                        size
                                                    }: { color: string; size: number }) => {
                                                        return <PencilIcon
                                                            color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                            size={size}
                                                        />;
                                                    }}
                                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                                    title={localize("members-list-edit-button")}
                                                    onPress={() => {
                                                        setEditId(item.id);
                                                        setFormData({
                                                            name_surname: item.name_surname,
                                                            address: item.address || "",
                                                            phone: item.phone || "",
                                                            email: item.email || ""
                                                        });
                                                        setModalVisible(true);
                                                    }}
                                                    type="neutral"
                                                />
                                                <Button
                                                    icon={({
                                                        color,
                                                        size
                                                    }: { color: string; size: number }) => {
                                                        return <TrashIcon
                                                            color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                            size={size}
                                                        />;
                                                    }}
                                                    onPress={() => handleDelete(item.id, item.name_surname)}
                                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                                    title={localize("members-list-delete-button")}
                                                    type="danger"
                                                />
                                            </View>
                                        </TouchableOpacity>;
                                    })}
                                    {renderPagination()}
                                </ScrollView>
                        }
                    </>
            }
            <Modal
                onOverlayPress={({
                    closeAnimation
                }: { closeAnimation: (cb: () => void) => void }) => closeAnimation(() => setPreviewItem(null))}
                onClosed={() => setPreviewItem(null)}
                portalName="modal-system"
                id="member-preview-modal"
                isActive={!!previewItem}
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("members-preview-title")}
                    </Text>
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text>{localize("members-preview-name", [
                            previewItem?.name_surname || ""
                        ])}</Text>
                        {previewItem?.email && <Text>{localize("members-preview-email", [
                            previewItem.email
                        ])}</Text>}
                        {previewItem?.phone && <Text>{localize("members-preview-phone", [
                            previewItem.phone
                        ])}</Text>}
                        {previewItem?.address && <Text>{localize("members-preview-address", [
                            previewItem.address
                        ])}</Text>}
                        <View
                            style={[
                                stylesheet.modalActions,
                                {
                                    marginTop: spaces.spacingXl,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Button
                                title={localize("members-preview-close")}
                                onPress={() => setPreviewItem(null)}
                                spreadBehaviour="stretch"
                                type="neutral"
                            />
                        </View>
                    </View>
                </View>
            </Modal>
            <Modal
                onOverlayPress={({
                    closeAnimation
                }: { closeAnimation: (cb: () => void) => void }) => closeAnimation(() => setModalVisible(false))}
                onClosed={() => setModalVisible(false)}
                portalName="modal-system"
                isActive={modalVisible}
                id="members-modal"
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {editId ? localize("members-modal-edit-title") : localize("members-modal-create-title")}
                    </Text>
                    <ScrollView
                        showsHorizontalScrollIndicator={false}
                        showsVerticalScrollIndicator={false}
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd
                            }
                        ]}
                        contentContainerStyle={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <TextInput
                            placeholder={localize("members-modal-name-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                name_surname: text
                            }))}
                            title={localize("members-modal-name-title")}
                            defaultValue={formData.name_surname}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            placeholder={localize("members-modal-email-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                email: text
                            }))}
                            title={localize("members-modal-email-title")}
                            defaultValue={formData.email}
                            keyboardType="email-address"
                            spreadBehaviour="stretch"
                            autoCapitalize="none"
                        />
                        <TextInput
                            placeholder={localize("members-modal-phone-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                phone: text
                            }))}
                            title={localize("members-modal-phone-title")}
                            defaultValue={formData.phone}
                            spreadBehaviour="stretch"
                            keyboardType="phone-pad"
                        />
                        <TextInput
                            placeholder={localize("members-modal-address-placeholder")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                address: text
                            }))}
                            title={localize("members-modal-address-title")}
                            defaultValue={formData.address}
                            spreadBehaviour="stretch"
                        />
                        <View
                            style={[
                                stylesheet.modalActions,
                                {
                                    flexDirection: isMobile ? "column-reverse" : "row",
                                    marginTop: spaces.spacingXl,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Button
                                title={localize("members-modal-cancel")}
                                onPress={() => setModalVisible(false)}
                                spreadBehaviour="stretch"
                                type="neutral"
                            />
                            <Button
                                title={localize("members-modal-save")}
                                spreadBehaviour="stretch"
                                isDisabled={isSubmitting}
                                onPress={handleSave}
                                type="primary"
                            />
                        </View>
                    </ScrollView>
                </View>
            </Modal>
        </View>
    </PageContainer>;
};

export default Members;
